import { Announcement } from 'base/accounts_service';
import { AccountStore } from 'base/account_store';
import { getLocalStorageItem } from 'base/localstorage';
import { Button } from 'base/ui/button';
import { DialogLayer } from 'base/ui/dialog';
import { IconClear } from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import { TextRegular, TextRegularLight, Title5 } from 'base/ui/typography';
import { observer } from 'mobx-react-lite';
import React from 'react';
import styles from './announcement.css';

export const MaybeAnnouncementDialog = observer(function _MAD({
  accountStore,
}: {
  accountStore: AccountStore;
}) {
  const [isOpen, setOpen] = React.useState(true);
  const value = accountStore.member?.announcement;
  if (!value || !isOpen) {
    return null;
  }
  const storageKey = `closedAnnouncement:${value.id}`;
  if (getLocalStorageItem(storageKey) === '1') {
    return null;
  }
  return (
    <AnnouncementDialog
      value={value}
      onClose={() => {
        setOpen(false);
        localStorage[storageKey] = '1';
      }}
      onLearnMore={() => {
        localStorage[storageKey] = '1';
      }}
    />
  );
});

export function AnnouncementDialog({
  value,
  onClose,
  onLearnMore,
}: {
  value: Announcement;
  onClose: () => void;
  onLearnMore: () => void;
}) {
  const [ratio, setRatio] = React.useState<number | undefined>(undefined);
  React.useEffect(() => {
    getImageRatio(value.imageUrl).then(setRatio);
  }, [value.imageUrl]);

  // Wait until the image loads to show the dialog, to avoid layout shifts.
  if (ratio == null) {
    return null;
  }

  return (
    <DialogLayer onClose={onClose}>
      <div className={styles.popup}>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            onClose();
          }}
          className={styles.closeButton}
        >
          <IconClear />
        </a>
        <img
          src={value.imageUrl}
          className={styles.image}
          style={{ aspectRatio: ratio }}
        />
        <div className={styles.contentWrapper}>
          <Rows spacing={16} xalign="center">
            <div className={styles.pretitle}>
              <TextRegular align="center">{value.pretitle}</TextRegular>
            </div>
            <Title5 align="center">{value.title}</Title5>
            <TextRegularLight align="center">{value.body}</TextRegularLight>
            <div />
            <Button
              size="large"
              variant="primary"
              label="Learn More"
              href={value.learnMoreUrl}
              onClick={onLearnMore}
            />
          </Rows>
        </div>
      </div>
    </DialogLayer>
  );
}

function getImageRatio(url: string) {
  const img = new Image();
  return new Promise<number>((resolve, reject) => {
    img.onload = () => resolve(img.naturalWidth / img.naturalHeight);
    img.onerror = reject;
    img.src = url;
  }).finally(() => {
    img.onload = null;
    img.onerror = null;
  });
}
